/**
 * Calls the backend POST /export/excel endpoint which returns a styled .xlsx
 * file (Apache POI) with:
 *   - Company header row (purple, bold)
 *   - Report title + sub-title rows
 *   - Purple column headers, white bold text
 *   - Alternating row shading
 *   - Auto-sized columns
 */

import { apiClient } from '@/api/client';
import { storage, StorageKeys } from '@/utils/storage';

export interface ExcelExportOptions {
  title: string;
  subTitle?: string;
  headers: string[];
  rows: (string | number | null | undefined)[][];
}

export async function downloadExcelFromBackend(opts: ExcelExportOptions): Promise<void> {
  const token = storage.get(StorageKeys.ACCESS_TOKEN);
  const payload = {
    title: opts.title,
    subTitle: opts.subTitle ?? `Generated: ${new Date().toLocaleDateString('en-IN')}`,
    headers: opts.headers,
    rows: opts.rows.map(r => r.map(cell => (cell == null ? '' : String(cell)))),
  };

  const response = await apiClient.post('/export/excel', payload, {
    responseType: 'arraybuffer',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  const blob = new Blob([response.data as ArrayBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const filename = opts.title.replace(/[^a-zA-Z0-9_\-]/g, '_').toLowerCase() + '.xlsx';
  triggerBlobDownload(blob, filename);
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
}
